type Deps = {
  raf: (fn: (t: number) => void) => number;
  cancelRaf: (handle: number) => void;
  now: () => number;
  hidden: () => boolean;
  /** `prefers-reduced-motion: reduce`, read every frame so a change mid-session lands. */
  reduced: () => boolean;
};

/** §8.4's frame-time budget. Over it, amplitude gives way before frames do. */
const BUDGET_MS = 4;
/** Never below this: a face that stops entirely reads as broken, not as busy. */
const FLOOR = 0.2;

/**
 * The one `requestAnimationFrame` loop (§8.4).
 *
 * One loop for the whole app rather than one per component: eight avatars on
 * the select screen would otherwise be eight loops competing with Whisper and
 * Kokoro for a 24GB machine. Dependencies are injected so the tests can drive
 * time instead of waiting for it.
 */
export function createClock(deps: Deps) {
  const subscribers = new Set<(t: number) => void>();
  let handle: number | null = null;
  let scale = 1;

  const tick = (t: number) => {
    handle = null;
    // PRD §11/D3: reduced motion outranks everything else here. One frame at
    // phase 0 — `blinkAt(0)` is eyes open, the mouth at rest — and no next
    // one, so every rig holds still without each learning a second rule.
    if (deps.reduced()) {
      for (const fn of subscribers) fn(0);
      return;
    }
    // Measured around the subscribers, not between frames. What the budget is
    // about is the cost this loop adds; a frame the browser was simply late to
    // call has cost nothing, and degrading on that would flatten the avatar
    // every time the tab regained focus.
    const started = deps.now();
    if (!deps.hidden()) {
      for (const fn of subscribers) fn(t);
    }
    const cost = deps.now() - started;
    // Down fast, up slow: recovering as eagerly as it degrades would oscillate
    // visibly on a machine sitting near the budget.
    scale = cost > BUDGET_MS ? Math.max(FLOOR, scale - 0.1) : Math.min(1, scale + 0.02);
    schedule();
  };

  const schedule = () => {
    if (handle === null && subscribers.size > 0) handle = deps.raf(tick);
  };

  return {
    subscribe(fn: (t: number) => void) {
      subscribers.add(fn);
      schedule();
      return () => {
        subscribers.delete(fn);
        if (subscribers.size === 0 && handle !== null) {
          deps.cancelRaf(handle);
          handle = null;
        }
      };
    },
    /** Multiplier on `--tutor-intensity`. 1 when the budget is being met, 0 under reduced motion. */
    intensityScale: () => (deps.reduced() ? 0 : scale),
    /** Re-arm the loop after reduced motion is switched off. */
    wake: () => schedule(),
    /** Whether motion is reduced — for what is timed rather than animated. */
    reduced: () => deps.reduced(),
    running: () => handle !== null,
  };
}

/**
 * The app's clock. One module-level instance is the point of the design.
 *
 * `null` where there is no `window` — a test render or any future SSR. The
 * avatar simply does not move there, which is the same thing reduced motion
 * asks for.
 */
// `matchMedia` is guarded separately from `window`: a test DOM can have one
// without the other, and a throw at import would take every test with it.
const reducedMotion =
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : null;

export const clock =
  typeof window === 'undefined'
    ? null
    : createClock({
        raf: (fn) => window.requestAnimationFrame(fn),
        cancelRaf: (h) => window.cancelAnimationFrame(h),
        now: () => performance.now(),
        hidden: () => document.hidden,
        reduced: () => reducedMotion?.matches ?? false,
      });

reducedMotion?.addEventListener('change', () => clock?.wake());
